/**
 * Gráficos — SVG próprio, sem biblioteca.
 *
 * São quatro formas (barra horizontal, barra vertical, linha acumulada e
 * comparação entre dois anos). Uma biblioteca de gráficos custaria ~100 kB
 * comprimidos para desenhar isso, traria um sistema de temas paralelo ao do
 * painel e ainda exigiria sobrescrever quase tudo para casar com a identidade.
 * O canvas da régua e o renderizador das landing pages já são feitos à mão pelo
 * mesmo motivo.
 *
 * ## Cores
 *
 * A paleta categórica é validada — azul da marca EGBA, laranja, verde-água —
 * contra separação para daltonismo (pior par adjacente ΔE 9,2 deutan, alvo ≥ 8)
 * e contra visão normal (ΔE 27,6, piso 15) sobre fundo branco. O verde-água
 * fica abaixo de 3:1 de contraste com o branco, então **toda série leva rótulo
 * direto**: identidade nunca depende só da cor.
 *
 * Quantidade é sempre **uma cor só** em rampa. Barra colorida por categoria
 * quando o eixo já diz a categoria é cor gasta à toa — e faz o olho procurar um
 * significado que não existe.
 */

import { useId, useState, type ReactNode } from 'react'

/**
 * Paleta categórica validada. Ordem fixa: nunca cicle nem gere um tom novo.
 *
 * As três primeiras passam todos os pares; a quarta foi validada para o caso
 * específico da barra empilhada (pares adjacentes, com folga de 2 px entre
 * segmentos e rótulo direto em cada um). Uma quinta série não se inventa: vira
 * "Outros" ou vira pequenos múltiplos.
 */
export const SERIES = ['#0c64b0', '#eb6834', '#1baf7a'] as const
export const SERIE_4 = '#7c5cd6'

/** Quantidade: um tom só. */
export const QUANTIDADE = '#0c64b0'
const GRADE = '#e8eef4'
const EIXO = '#94a3b8'

function Dica({ x, y, children }: { x: number; y: number; children: ReactNode }) {
  return (
    <div
      className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs whitespace-nowrap text-white shadow-lg"
      style={{ left: x, top: y - 8 }}
    >
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Barras horizontais — ranking (município, etapa, porte)
// ---------------------------------------------------------------------------

export interface ItemBarra {
  rotulo: string
  valor: number
  /** Texto à direita. Sem ele o eixo vira adivinhação. */
  formatado: string
  detalhe?: string
}

export function BarrasHorizontais({
  itens,
  larguraRotulo = 168,
  cor = QUANTIDADE,
}: {
  itens: ItemBarra[]
  larguraRotulo?: number
  cor?: string
}) {
  const [sobre, setSobre] = useState<number | null>(null)
  const maior = Math.max(1, ...itens.map((i) => i.valor))

  return (
    <div className="relative space-y-1">
      {itens.map((it, i) => (
        <div
          key={it.rotulo}
          className="flex items-center gap-3"
          onMouseEnter={() => setSobre(i)}
          onMouseLeave={() => setSobre(null)}
        >
          <span
            className="shrink-0 truncate text-[13px] text-slate-600"
            style={{ width: larguraRotulo }}
            title={it.rotulo}
          >
            {it.rotulo}
          </span>
          <span className="relative h-6 flex-1 overflow-hidden rounded bg-slate-50">
            <span
              className="absolute inset-y-0 left-0 transition-[width] duration-500"
              style={{
                width: `${Math.max(1.5, (it.valor / maior) * 100)}%`,
                background: cor,
                // Ponta arredondada só na extremidade do dado; a base fica
                // ancorada na linha zero.
                borderRadius: '0 4px 4px 0',
                opacity: sobre === null || sobre === i ? 1 : 0.45,
              }}
            />
          </span>
          <span className="w-28 shrink-0 text-right text-xs font-medium text-slate-700 tabular-nums">
            {it.formatado}
          </span>
          {it.detalhe && (
            <span className="w-20 shrink-0 text-right text-xs text-slate-400 tabular-nums">
              {it.detalhe}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Barras verticais — distribuição por faixa
// ---------------------------------------------------------------------------

export function Distribuicao({
  itens,
  altura = 180,
  cor = QUANTIDADE,
}: {
  itens: Array<{ rotulo: string; valor: number; dica: string }>
  altura?: number
  cor?: string
}) {
  const [sobre, setSobre] = useState<{ i: number; x: number; y: number } | null>(null)
  const maior = Math.max(1, ...itens.map((i) => i.valor))

  return (
    <div className="relative">
      <div className="flex items-end gap-2" style={{ height: altura }}>
        {itens.map((it, i) => (
          <div key={it.rotulo} className="flex h-full flex-1 flex-col justify-end">
            <span className="mb-1 text-center text-xs font-medium text-slate-700 tabular-nums">
              {it.valor.toLocaleString('pt-BR')}
            </span>
            <span
              className="w-full transition-[height] duration-500"
              style={{
                height: `${Math.max(2, (it.valor / maior) * 100)}%`,
                background: cor,
                borderRadius: '4px 4px 0 0',
                opacity: sobre === null || sobre.i === i ? 1 : 0.45,
              }}
              onMouseEnter={(e) => {
                const r = (e.target as HTMLElement).getBoundingClientRect()
                const pai = (e.currentTarget as HTMLElement).closest('.relative')!.getBoundingClientRect()
                setSobre({ i, x: r.left - pai.left + r.width / 2, y: r.top - pai.top })
              }}
              onMouseLeave={() => setSobre(null)}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2 border-t border-[color:var(--color-borda-suave)] pt-2">
        {itens.map((it) => (
          <span key={it.rotulo} className="flex-1 text-center text-[11px] leading-tight text-slate-500">
            {it.rotulo}
          </span>
        ))}
      </div>
      {sobre && (
        <Dica x={sobre.x} y={sobre.y}>
          {itens[sobre.i]!.dica}
        </Dica>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Curva acumulada — concentração de mercado
// ---------------------------------------------------------------------------

export function CurvaConcentracao({
  pontos,
  marcador,
  altura = 220,
}: {
  pontos: Array<{ pctEscolas: number; pctValor: number; escolas: number; valorCentavos?: number }>
  /** Linha de referência: as N escolas que fazem 80 % do valor. */
  marcador?: { pctEscolas: number; rotulo: string }
  altura?: number
}) {
  const id = useId()
  const [sobre, setSobre] = useState<number | null>(null)
  const L = 44
  const B = 26
  const W = 720
  const H = altura

  const x = (p: number): number => L + (p / 100) * (W - L - 8)
  const y = (p: number): number => H - B - (p / 100) * (H - B - 10)

  if (pontos.length === 0) return null

  const linha = pontos.map((p) => `${x(p.pctEscolas)},${y(p.pctValor)}`).join(' ')
  const area = `${x(0)},${y(0)} ${linha} ${x(pontos[pontos.length - 1]!.pctEscolas)},${y(0)}`
  const perto = sobre !== null ? pontos[sobre] : null

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img"
        aria-label="Curva de concentração: percentual do valor acumulado conforme se somam escolas, da maior para a menor.">
        <defs>
          <linearGradient id={`g-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES[0]} stopOpacity="0.18" />
            <stop offset="100%" stopColor={SERIES[0]} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line x1={L} x2={W - 8} y1={y(v)} y2={y(v)} stroke={GRADE} strokeWidth="1" />
            <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill={EIXO}>
              {v}%
            </text>
          </g>
        ))}

        {/* Diagonal: como seria se todas as escolas valessem o mesmo. É a
            referência que transforma "a curva sobe rápido" em "quanto rápido". */}
        <line
          x1={x(0)} y1={y(0)} x2={x(100)} y2={y(100)}
          stroke={EIXO} strokeWidth="1.5" strokeDasharray="4 4" opacity="0.55"
        />

        <polygon points={area} fill={`url(#g-${id})`} />
        <polyline points={linha} fill="none" stroke={SERIES[0]} strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" />

        {marcador && (
          <g>
            <line x1={x(marcador.pctEscolas)} x2={x(marcador.pctEscolas)} y1={y(0)} y2={y(80)}
              stroke={SERIES[1]} strokeWidth="1.5" strokeDasharray="3 3" />
            <line x1={L} x2={x(marcador.pctEscolas)} y1={y(80)} y2={y(80)}
              stroke={SERIES[1]} strokeWidth="1.5" strokeDasharray="3 3" />
            <circle cx={x(marcador.pctEscolas)} cy={y(80)} r="4.5"
              fill={SERIES[1]} stroke="#fff" strokeWidth="2" />
            {/* Abaixo da linha de 80 %, com halo branco: acima, o texto caía
                em cima da própria curva e as duas coisas ficavam ilegíveis. */}
            <text
              x={x(marcador.pctEscolas) + 10} y={y(80) + 18} fontSize="11"
              fill="#9a3412" fontWeight="600"
              stroke="#fff" strokeWidth="3" paintOrder="stroke"
            >
              {marcador.rotulo}
            </text>
          </g>
        )}

        {perto && (
          <circle cx={x(perto.pctEscolas)} cy={y(perto.pctValor)} r="4"
            fill={SERIES[0]} stroke="#fff" strokeWidth="2" />
        )}

        {[0, 25, 50, 75, 100].map((v) => (
          <text key={v} x={x(v)} y={H - 6} textAnchor="middle" fontSize="11" fill={EIXO}>
            {v}%
          </text>
        ))}

        <rect
          x={L} y={0} width={W - L - 8} height={H - B} fill="transparent"
          onMouseMove={(e) => {
            const cx = e.currentTarget.getBoundingClientRect()
            const pct = ((e.clientX - cx.left) / cx.width) * 100
            let melhor = 0
            for (let i = 0; i < pontos.length; i += 1) {
              if (Math.abs(pontos[i]!.pctEscolas - pct) < Math.abs(pontos[melhor]!.pctEscolas - pct)) {
                melhor = i
              }
            }
            setSobre(melhor)
          }}
          onMouseLeave={() => setSobre(null)}
        />
      </svg>

      <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
        <span>← escolas ordenadas da maior para a menor</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4 rounded" style={{ background: SERIES[0] }} />
            valor acumulado
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-0 w-4 border-t border-dashed border-slate-400" />
            mercado distribuído por igual
          </span>
        </span>
      </div>

      {perto && (
        <div className="pointer-events-none absolute top-2 right-2 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs text-white shadow-lg">
          {perto.escolas.toLocaleString('pt-BR')} escolas ={' '}
          <strong>{Math.round(perto.pctValor)}%</strong> do valor
          {perto.valorCentavos !== undefined && (
            <span className="block text-[11px] text-slate-300">
              {(perto.valorCentavos / 100).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                maximumFractionDigits: 0,
              })}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Comparação entre dois anos — série curta
// ---------------------------------------------------------------------------

/**
 * Evolução de várias medidas — **pequenos múltiplos**, não uma linha só.
 *
 * As medidas têm ordens de grandeza diferentes: numa escola, o total de
 * matrículas passa de oito mil e a 3ª série tem trezentas. Postas no mesmo eixo,
 * as duas menores viram uma linha reta colada no zero e os rótulos se sobrepõem
 * — foi exatamente o que aconteceu na primeira versão desta ficha.
 *
 * A saída errada seria um segundo eixo à direita: dois eixos convidam a comparar
 * inclinações que não são comparáveis, e a inclinação passa a depender de onde
 * cada escala foi cortada. A saída certa é um quadro por medida, cada um com a
 * própria escala e a própria variação escrita — a comparação vira a variação
 * percentual, que é o que se quer saber.
 */
export function Evolucao({
  series,
  anos,
}: {
  series: Array<{ nome: string; valores: number[] }>
  anos: number[]
}) {
  const W = 200
  const H = 74

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {series.map((s, si) => {
        const cor = SERIES[si % SERIES.length]
        const maior = Math.max(1, ...s.valores)
        const primeiro = s.valores[0] ?? 0
        const ultimo = s.valores[s.valores.length - 1] ?? 0
        const variacao = primeiro > 0 ? ((ultimo - primeiro) / primeiro) * 100 : null

        const x = (i: number): number =>
          s.valores.length === 1 ? W / 2 : 6 + (i / (s.valores.length - 1)) * (W - 12)
        // Base própria: a escala vai de zero ao maior valor DESTA medida.
        const y = (v: number): number => H - 10 - (v / maior) * (H - 24)

        return (
          <div key={s.nome} className="rounded-lg border border-[color:var(--color-borda)] p-3">
            <p className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
              <span
                className="inline-block size-2 rounded-full"
                style={{ background: cor }}
                aria-hidden="true"
              />
              {s.nome}
            </p>
            <p className="mt-0.5 text-xl font-semibold text-slate-900 tabular-nums">
              {ultimo.toLocaleString('pt-BR')}
            </p>

            <svg viewBox={`0 0 ${W} ${H}`} className="mt-1 w-full" role="img"
              aria-label={`${s.nome}: ${s.valores.map((v, i) => `${anos[i]} ${v}`).join(', ')}.`}>
              <line x1="6" x2={W - 6} y1={y(0)} y2={y(0)} stroke={GRADE} strokeWidth="1" />
              {s.valores.length > 1 && (
                <polyline
                  points={s.valores.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
                  fill="none" stroke={cor} strokeWidth="2" strokeLinecap="round"
                />
              )}
              {s.valores.map((v, i) => (
                <circle key={i} cx={x(i)} cy={y(v)} r="4" fill={cor} stroke="#fff" strokeWidth="2" />
              ))}
              {s.valores.map((v, i) => (
                <text
                  key={`r-${i}`} x={x(i)} y={y(v) - 8}
                  textAnchor={i === 0 ? 'start' : 'end'}
                  fontSize="10" fill="#475569" fontWeight="600"
                >
                  {v.toLocaleString('pt-BR')}
                </text>
              ))}
            </svg>

            <div className="mt-1 flex items-center justify-between text-[10px] text-slate-400">
              <span>{anos[0]}</span>
              {variacao !== null && (
                <span
                  className={
                    Math.abs(variacao) < 1
                      ? 'font-semibold text-slate-500'
                      : variacao > 0
                        ? 'font-semibold text-emerald-700'
                        : 'font-semibold text-red-700'
                  }
                >
                  {variacao > 0 ? '+' : ''}
                  {Math.round(variacao)}%
                </span>
              )}
              <span>{anos[anos.length - 1]}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Barra empilhada — composição
// ---------------------------------------------------------------------------

export function Composicao({
  partes,
}: {
  partes: Array<{ rotulo: string; valor: number; cor?: string }>
}) {
  const total = partes.reduce((s, p) => s + p.valor, 0)
  if (total === 0) return <p className="text-sm text-slate-400">Sem matrículas nas etapas que concluem.</p>

  const visiveis = partes.filter((p) => p.valor > 0)

  return (
    <div>
      {/* 2px de folga entre segmentos: sem isso dois tons vizinhos viram um só. */}
      <div className="flex h-7 gap-[2px] overflow-hidden rounded-lg">
        {visiveis.map((p, i) => (
          <span
            key={p.rotulo}
            title={`${p.rotulo}: ${p.valor.toLocaleString('pt-BR')}`}
            style={{
              width: `${(p.valor / total) * 100}%`,
              background: p.cor ?? SERIES[i % SERIES.length],
            }}
          />
        ))}
      </div>
      <ul className="mt-3 space-y-1">
        {visiveis.map((p, i) => (
          <li key={p.rotulo} className="flex items-center gap-2 text-sm">
            <span
              className="inline-block size-2.5 shrink-0 rounded-sm"
              style={{ background: p.cor ?? SERIES[i % SERIES.length] }}
              aria-hidden="true"
            />
            <span className="flex-1 text-slate-600">{p.rotulo}</span>
            <span className="font-medium text-slate-800 tabular-nums">
              {p.valor.toLocaleString('pt-BR')}
            </span>
            <span className="w-12 text-right text-xs text-slate-400 tabular-nums">
              {Math.round((p.valor / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Posição num ranking
// ---------------------------------------------------------------------------

export function Posicao({ acima, total }: { acima: number; total: number }) {
  if (total === 0) return null
  const posicao = acima + 1
  const pct = (posicao / total) * 100

  return (
    <div>
      <div className="relative h-2 rounded-full bg-slate-100">
        <span
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${Math.max(1, 100 - pct)}%`, background: SERIES[0] }}
        />
        <span
          className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
          style={{ left: `${Math.max(1, 100 - pct)}%`, background: SERIES[1] }}
        />
      </div>
      <p className="mt-2 text-sm text-slate-600">
        <strong className="text-slate-900">{posicao.toLocaleString('pt-BR')}ª</strong> de{' '}
        {total.toLocaleString('pt-BR')} escolas do mercado — está entre as{' '}
        <strong className="text-slate-900">{Math.max(1, Math.round(pct))}% maiores</strong>.
      </p>
    </div>
  )
}
